import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Box, Typography, List, ListItemButton, ListItemAvatar, ListItemText, Avatar,
  TextField, IconButton, CircularProgress, Alert, Paper, Dialog, DialogTitle,
  DialogContent, DialogActions, Divider, useMediaQuery, Button, Chip, Tabs, Tab,
  Checkbox, ListItem,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ReplyIcon from '@mui/icons-material/Reply';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddCommentIcon from '@mui/icons-material/AddComment';
import GroupsIcon from '@mui/icons-material/Groups';
import Diversity3Icon from '@mui/icons-material/Diversity3';
import CottageIcon from '@mui/icons-material/Cottage';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DoneIcon from '@mui/icons-material/Done';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { Badge, Menu, MenuItem, ListItemIcon as MenuItemIcon, ListItemText as MenuItemText } from '@mui/material';
import { io } from 'socket.io-client';
import {
  API_URL, getToken, getSavedUser, getConversations, getMessages, getChatMembers, getRoomMembers, getRoomReceipts,
  createGroup, getGroupInfo, updateGroupMembers, leaveGroup, deleteGroup,
  markRoomRead, uploadChatAttachment,
} from '../api';
import { getCached, setCached } from '../cache';

const dmRoomFor = (idA, idB) => `dm:${[String(idA), String(idB)].sort().join(':')}`;

const photoThumb = (url, size = 64) =>
  url && typeof url === 'string' && url.includes('/upload/')
    ? url.replace('/upload/', `/upload/w_${size},h_${size},c_fill,g_face/`)
    : url;

const GROUP_COLORS = ['#059669', '#065F46', '#047857'];
const groupColor = (name = '') => GROUP_COLORS[(name.charCodeAt(0) || 0) % GROUP_COLORS.length];

const timeLabel = (d) => {
  if (!d) return '';
  const date = new Date(d);
  const sameDay = date.toDateString() === new Date().toDateString();
  return sameDay
    ? date.toLocaleTimeString('en-KE', { hour: '2-digit', minute: '2-digit' })
    : date.toLocaleDateString('en-KE', { day: 'numeric', month: 'short' });
};

const ChatPage = ({ currentUser }) => {
  const myId = String(currentUser?.id || currentUser?._id || getSavedUser()?._id || '');
  const isMobile = useMediaQuery('(max-width:700px)');

  const [conversations, setConversations] = useState([]);
  const [active, setActive] = useState(null); // { room, title, kind, groupId? }
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [replyTarget, setReplyTarget] = useState(null); // the message being replied to
  const [editingMessage, setEditingMessage] = useState(null); // message currently being edited
  const [pendingAttachment, setPendingAttachment] = useState(null); // { file, previewUrl, type, name }
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuMessage, setMenuMessage] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // the message pending a delete choice
  const fileInputRef = useRef(null);
  const [highlightedId, setHighlightedId] = useState(null); // briefly glows when jumped-to
  const messageRefs = useRef({}); // _id -> DOM node, for "jump to quoted message"

  const jumpToMessage = (id) => {
    const el = messageRefs.current[id];
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setHighlightedId(id);
    setTimeout(() => setHighlightedId((cur) => (cur === id ? null : cur)), 1500);
  };
  const [loadingList, setLoadingList] = useState(true);
  const [loadingThread, setLoadingThread] = useState(false);
  const [error, setError] = useState('');
  const [members, setMembers] = useState([]);
  const [onlineUserIds, setOnlineUserIds] = useState(() => new Set());
  const [roomMembers, setRoomMembers] = useState([]);
  const [roomReceipts, setRoomReceipts] = useState([]); // [{userId, lastReadAt, lastDeliveredAt}]
  const [roomMembersOpen, setRoomMembersOpen] = useState(false);
  const [loadingRoomMembers, setLoadingRoomMembers] = useState(false);

  // New-conversation dialog
  const [newOpen, setNewOpen] = useState(false);
  const [newTab, setNewTab] = useState(0); // 0 = group, 1 = direct
  const [groupName, setGroupName] = useState('');
  const [pickedIds, setPickedIds] = useState([]);
  const [creating, setCreating] = useState(false);

  // Group info dialog
  const [infoOpen, setInfoOpen] = useState(false);

  const socketRef = useRef(null);
  const bottomRef = useRef(null);
  const activeRoomRef = useRef(null);
  activeRoomRef.current = active?.room || null;

  const loadConversations = useCallback(async () => {
    // Show last-known conversations instantly (with their unread badges),
    // then quietly refresh — so opening Chat never starts on a blank list
    // while the network catches up.
    const cached = getCached('conversations');
    if (cached && cached.data && cached.data.length > 0) {
      setConversations(cached.data);
      setLoadingList(false);
    }
    try {
      const fresh = await getConversations();
      setConversations(fresh);
      setCached('conversations', fresh);
    } catch (err) {
      if (!cached) setError(err.response?.data?.error || 'Could not load conversations.');
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    const socket = io(API_URL, { auth: { token: getToken() } });
    socketRef.current = socket;
    socket.on('new_message', (msg) => {
      if (msg.room === activeRoomRef.current) {
        setMessages((prev) => [...prev, msg]);
        markRoomRead(msg.room).catch(() => {}); // I'm looking at it — stay read
      }
      loadConversations();
    });
    socket.on('message_edited', ({ _id, text: newText, editedAt }) => {
      setMessages((prev) => prev.map((m) => (m._id === _id ? { ...m, text: newText, editedAt } : m)));
    });
    socket.on('message_deleted', ({ _id, forEveryone }) => {
      if (forEveryone) {
        setMessages((prev) => prev.map((m) => (m._id === _id ? { ...m, deletedForEveryone: true, text: '', attachment: null } : m)));
      } else {
        // "Delete for me" — this event only ever reaches the requester
        setMessages((prev) => prev.filter((m) => m._id !== _id));
      }
    });
    socket.on('conversation_update', () => loadConversations());
    socket.on('presence_snapshot', (ids) => {
      setOnlineUserIds(new Set(ids.map(String)));
    });
    socket.on('presence_update', ({ userId, online }) => {
      setOnlineUserIds((prev) => {
        const next = new Set(prev);
        if (online) next.add(String(userId)); else next.delete(String(userId));
        return next;
      });
    });
    socket.on('read_receipt_update', ({ room }) => {
      // Only refetch if it's the conversation currently open — no point
      // pulling receipts for rooms the user isn't even looking at.
      if (room === activeRoomRef.current) {
        getRoomReceipts(room).then(setRoomReceipts).catch(() => {});
      }
    });
    socket.on('connect_error', (err) => {
      if (String(err.message).toLowerCase().includes('auth')) {
        setError('Chat connection failed — please log in again.');
      }
    });
    loadConversations();
    return () => socket.disconnect();
  }, [loadConversations]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const openRoom = async (conv) => {
    setActive(conv);
    setMessages([]);
    setLoadingThread(true);
    setError('');
    // Clear this room's unread badge immediately (optimistic)
    setConversations((prev) => prev.map((c) => (c.room === conv.room ? { ...c, unread: 0 } : c)));
    markRoomRead(conv.room).catch(() => {});
    setRoomMembers([]);
    setRoomReceipts([]);
    if (conv.kind !== 'dm') {
      getRoomMembers(conv.room).then((data) => setRoomMembers(data.members || [])).catch(() => {});
    }
    getRoomReceipts(conv.room).then(setRoomReceipts).catch(() => {});
    try {
      socketRef.current?.emit('join_room', conv.room);
      setMessages(await getMessages(conv.room));
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load this conversation.');
    } finally {
      setLoadingThread(false);
    }
  };

  const send = () => {
    const value = text.trim();

    // Editing an existing message takes over the input box entirely
    if (editingMessage) {
      if (!value) return;
      socketRef.current?.emit('edit_message', { messageId: editingMessage._id, text: value }, (resp) => {
        if (resp?.error) setError(resp.error);
      });
      setEditingMessage(null);
      setText('');
      return;
    }

    if (!value && !pendingAttachment) return;
    if (!active) return;

    const payload = { room: active.room, text: value };
    if (replyTarget) {
      payload.replyTo = {
        message: replyTarget._id,
        senderName: replyTarget.sender?.name || '',
        text: replyTarget.text || '',
      };
    }
    if (pendingAttachment?.uploaded) {
      payload.attachment = pendingAttachment.uploaded;
    }

    setText('');
    setReplyTarget(null);
    setPendingAttachment(null);
    socketRef.current?.emit('send_message', payload, (resp) => {
      if (resp?.error) setError(resp.error);
    });
  };

  const handleFilePick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow picking the same file again later
    if (!file) return;

    const isImage = file.type.startsWith('image/');
    const isPdf = file.type === 'application/pdf';
    if (!isImage && !isPdf) {
      setError('Please choose an image or a PDF file.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('That file is too large (maximum 10 MB).');
      return;
    }

    const previewUrl = isImage ? URL.createObjectURL(file) : null;
    setPendingAttachment({ file, previewUrl, type: isImage ? 'image' : 'pdf', name: file.name, uploaded: null });
    setUploadingAttachment(true);
    setError('');
    try {
      const data = await uploadChatAttachment(file);
      setPendingAttachment((prev) => (prev ? { ...prev, uploaded: data.attachment } : prev));
    } catch (err) {
      setError(err.response?.data?.error || 'Could not upload the file.');
      setPendingAttachment(null);
    } finally {
      setUploadingAttachment(false);
    }
  };

  const openMessageMenu = (event, message) => {
    setMenuAnchor(event.currentTarget);
    setMenuMessage(message);
  };
  const closeMessageMenu = () => { setMenuAnchor(null); setMenuMessage(null); };

  const startEdit = () => {
    if (!menuMessage) return;
    setEditingMessage(menuMessage);
    setText(menuMessage.text || '');
    setReplyTarget(null);
    setPendingAttachment(null);
    closeMessageMenu();
  };

  const askDelete = () => {
    if (!menuMessage) return;
    setDeleteConfirm(menuMessage);
    closeMessageMenu();
  };

  const doDelete = (forEveryone) => {
    if (!deleteConfirm) return;
    socketRef.current?.emit('delete_message', { messageId: deleteConfirm._id, forEveryone }, (resp) => {
      if (resp?.error) setError(resp.error);
      else if (!forEveryone) setMessages((prev) => prev.filter((m) => m._id !== deleteConfirm._id));
    });
    setDeleteConfirm(null);
  };

  const openNew = async () => {
    setNewOpen(true);
    setGroupName('');
    setPickedIds([]);
    setNewTab(0);
    try {
      setMembers(await getChatMembers());
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load the family directory.');
    }
  };

  const togglePick = (id) =>
    setPickedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const handleCreateGroup = async () => {
    setCreating(true);
    setError('');
    try {
      const data = await createGroup(groupName, pickedIds);
      setNewOpen(false);
      await loadConversations();
      openRoom({ room: `group:${data.group._id}`, title: data.group.name, kind: 'group', groupId: data.group._id });
    } catch (err) {
      setError(err.response?.data?.error || 'Could not create the group.');
    } finally {
      setCreating(false);
    }
  };

  const startDm = (member) => {
    setNewOpen(false);
    openRoom({ room: dmRoomFor(myId, member.id), title: member.name, kind: 'dm' });
  };

  const convIcon = (c) => {
    if (c.kind === 'family') return <Avatar sx={{ bgcolor: '#065F46' }}><GroupsIcon /></Avatar>;
    if (c.kind === 'house') return <Avatar sx={{ bgcolor: '#059669' }}><CottageIcon /></Avatar>;
    if (c.kind === 'musyi') return <Avatar sx={{ bgcolor: '#047857' }}><AccountTreeIcon /></Avatar>;
    if (c.kind === 'kwa') return <Avatar sx={{ bgcolor: '#065F46' }}><AccountTreeIcon /></Avatar>;
    if (c.kind === 'group') return <Avatar sx={{ bgcolor: groupColor(c.title) }}><Diversity3Icon /></Avatar>;
    return <Avatar src={c.photo ? photoThumb(c.photo) : undefined}>{(c.title || '?').charAt(0)}</Avatar>;
  };

  const listPane = (
    <Box sx={{ width: isMobile ? '100%' : 330, borderRight: isMobile ? 'none' : '1px solid #eee', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Chats</Typography>
        <Button size="small" variant="contained" startIcon={<AddCommentIcon />} onClick={openNew}
          sx={{ background: 'linear-gradient(135deg, #059669 0%, #065F46 100%)', textTransform: 'none' }}>
          New
        </Button>
      </Box>
      <Divider />
      {loadingList ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress size={28} /></Box>
      ) : (
        <List sx={{ overflowY: 'auto', flex: 1 }}>
          {conversations.map((c) => (
            <ListItemButton key={c.room} selected={c.room === active?.room} onClick={() => openRoom(c)} sx={{ py: 1.2 }}>
              <ListItemAvatar>{convIcon(c)}</ListItemAvatar>
              <ListItemText
                primary={c.title}
                secondary={
                  c.lastMessage
                    ? `${c.lastMessage.sender?.name?.split(' ')[0] || ''}: ${c.lastMessage.text}`.slice(0, 42)
                    : c.kind === 'family' ? 'Say hello to everyone'
                      : c.kind === 'house' ? 'Your house — talk to your people'
                        : c.kind === 'musyi' ? 'Your homestead — sons and daughters of this musyi'
                        : c.kind === 'kwa' ? 'Your close family line'
                          : c.kind === 'group' ? 'New group — say something' : ''
                }
                primaryTypographyProps={{ fontWeight: 600 }}
              />
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5, ml: 1 }}>
                <Typography variant="caption" color="text.secondary">{timeLabel(c.lastMessage?.createdAt)}</Typography>
                {c.unread > 0 && (
                  <Box sx={{
                    minWidth: 20, height: 20, px: 0.5, borderRadius: 10,
                    bgcolor: '#059669', color: '#fff', fontSize: 12, fontWeight: 700,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {c.unread > 99 ? '99+' : c.unread}
                  </Box>
                )}
              </Box>
            </ListItemButton>
          ))}
        </List>
      )}
    </Box>
  );

  const liveRoomMembers = roomMembers
    .map((m) => ({ ...m, online: onlineUserIds.has(String(m.id)) }))
    .sort((a, b) => Number(b.online) - Number(a.online));
  const liveOnlineCount = liveRoomMembers.filter((m) => m.online).length;

  // WhatsApp's own rule for groups: the blue "read" tick only appears once
  // EVERY other member has read it; grey double-tick once EVERY other
  // member has at least received it. For a DM, "every other member" is
  // just the one other person, so this naturally covers both cases.
  const getTickState = (m) => {
    if (roomReceipts.length === 0) return 'sent';
    const t = new Date(m.createdAt).getTime();
    if (roomReceipts.every((r) => new Date(r.lastReadAt).getTime() >= t)) return 'read';
    if (roomReceipts.every((r) => new Date(r.lastDeliveredAt).getTime() >= t)) return 'delivered';
    return 'sent';
  };

  const threadPane = (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      {!active ? (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', color: 'text.secondary', p: 2, textAlign: 'center' }}>
          <GroupsIcon sx={{ fontSize: 56, mb: 1 }} />
          <Typography>Select a conversation, open the Mbaa Mwendwa room, or create a group for your household, branch, or committee.</Typography>
        </Box>
      ) : (
        <>
          <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid #eee' }}>
            {isMobile && (
              <IconButton onClick={() => setActive(null)} aria-label="Back to chats"><ArrowBackIcon /></IconButton>
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap>{active.title}</Typography>
              {active.kind !== 'dm' && roomMembers.length > 0 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                  onClick={() => (active.kind === 'group' ? setInfoOpen(true) : setRoomMembersOpen(true))}
                >
                  {liveOnlineCount} online • {roomMembers.length} member{roomMembers.length === 1 ? '' : 's'}
                </Typography>
              )}
            </Box>
            {active.kind === 'family' && <Chip label="Everyone" size="small" />}
            {active.kind === 'house' && <Chip label="Nyumba" size="small" sx={{ bgcolor: '#D1FAE5', color: '#065F46', fontWeight: 600 }} />}
            {active.kind === 'musyi' && <Chip label="Musyi" size="small" sx={{ bgcolor: '#D1FAE5', color: '#065F46', fontWeight: 600 }} />}
            {active.kind === 'kwa' && <Chip label="Family line" size="small" sx={{ bgcolor: '#D1FAE5', color: '#065F46', fontWeight: 600 }} />}
            {active.kind !== 'dm' && active.kind !== 'group' && (
              <IconButton onClick={() => setRoomMembersOpen(true)} aria-label="Room members">
                <InfoOutlinedIcon />
              </IconButton>
            )}
            {active.kind === 'group' && (
              <IconButton onClick={() => setInfoOpen(true)} aria-label="Group details">
                <InfoOutlinedIcon />
              </IconButton>
            )}
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', p: 2, bgcolor: '#f7f7fb' }}>
            {loadingThread ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress size={28} /></Box>
            ) : (
              messages.map((m) => {
                const mine = String(m.sender?.user) === myId;
                const showName = !mine && active.kind !== 'dm';
                return (
                  <SwipeableMessage
                    key={m._id}
                    message={m}
                    mine={mine}
                    showName={showName}
                    tickState={mine ? getTickState(m) : null}
                    highlighted={highlightedId === m._id}
                    onReply={() => setReplyTarget(m)}
                    onJumpToQuote={() => m.replyTo?.message && jumpToMessage(m.replyTo.message)}
                    onOpenMenu={(e) => openMessageMenu(e, m)}
                    registerRef={(el) => { if (el) messageRefs.current[m._id] = el; }}
                  />
                );
              })
            )}
            <div ref={bottomRef} />
          </Box>

          {editingMessage && (
            <Box sx={{ px: 1.5, pt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ flex: 1, borderLeft: '3px solid #047857', pl: 1, bgcolor: '#f1f5f4', borderRadius: 1, py: 0.5, minWidth: 0 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', color: '#065F46' }}>
                  Editing your message
                </Typography>
              </Box>
              <IconButton size="small" onClick={() => { setEditingMessage(null); setText(''); }} aria-label="Cancel edit">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
          {replyTarget && (
            <Box sx={{ px: 1.5, pt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ flex: 1, borderLeft: '3px solid #059669', pl: 1, bgcolor: '#f1f5f4', borderRadius: 1, py: 0.5, minWidth: 0 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', color: '#065F46' }}>
                  Replying to {replyTarget.sender?.name}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {replyTarget.text}
                </Typography>
              </Box>
              <IconButton size="small" onClick={() => setReplyTarget(null)} aria-label="Cancel reply">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
          {pendingAttachment && (
            <Box sx={{ px: 1.5, pt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{
                flex: 1, display: 'flex', alignItems: 'center', gap: 1,
                borderLeft: '3px solid #059669', pl: 1, bgcolor: '#f1f5f4', borderRadius: 1, py: 0.5, minWidth: 0,
              }}>
                {pendingAttachment.type === 'image' ? (
                  <Box component="img" src={pendingAttachment.previewUrl} sx={{ width: 36, height: 36, borderRadius: 1, objectFit: 'cover' }} />
                ) : (
                  <PictureAsPdfIcon sx={{ color: '#c62828' }} />
                )}
                <Typography variant="caption" sx={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {uploadingAttachment ? `Uploading ${pendingAttachment.name}…` : pendingAttachment.name}
                </Typography>
                {uploadingAttachment && <CircularProgress size={16} />}
              </Box>
              <IconButton size="small" onClick={() => setPendingAttachment(null)} aria-label="Remove attachment">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
          <Box sx={{ p: 1.5, display: 'flex', gap: 1, borderTop: '1px solid #eee' }}>
            <input
              type="file" ref={fileInputRef} accept="image/*,application/pdf" hidden
              onChange={handleFilePick}
            />
            {!editingMessage && (
              <IconButton onClick={() => fileInputRef.current?.click()} aria-label="Attach a photo or PDF"
                sx={{ alignSelf: 'flex-end', color: '#059669' }}>
                <AttachFileIcon />
              </IconButton>
            )}
            <TextField
              fullWidth size="small" placeholder={editingMessage ? 'Edit your message…' : 'Write a message…'} value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              multiline maxRows={4}
            />
            <IconButton color="primary" onClick={send}
              disabled={editingMessage ? !text.trim() : (!text.trim() && !pendingAttachment) || uploadingAttachment}
              aria-label="Send"
              sx={{ bgcolor: '#059669', color: '#fff', '&:hover': { bgcolor: '#047857' }, width: 44, height: 44, alignSelf: 'flex-end' }}>
              <SendIcon fontSize="small" />
            </IconButton>
          </Box>
        </>
      )}
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', height: isMobile ? 'calc(100vh - 118px)' : 'calc(100vh - 96px)', display: 'flex', flexDirection: 'column', p: { xs: 0, sm: 2 } }}>
      {error && <Alert severity="error" onClose={() => setError('')} sx={{ m: 1 }}>{error}</Alert>}
      <Paper sx={{ flex: 1, display: 'flex', borderRadius: { xs: 0, sm: 3 }, overflow: 'hidden' }}>
        {isMobile ? (active ? threadPane : listPane) : (<>{listPane}{threadPane}</>)}
      </Paper>

      {/* New conversation */}
      <Dialog open={newOpen} onClose={() => setNewOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ pb: 0 }}>Start a conversation</DialogTitle>
        <Tabs value={newTab} onChange={(e, v) => setNewTab(v)} variant="fullWidth" sx={{ px: 2 }}>
          <Tab label="New group" />
          <Tab label="Direct message" />
        </Tabs>
        <DialogContent dividers sx={{ p: 0 }}>
          {newTab === 0 ? (
            <Box>
              <Box sx={{ p: 2, pb: 1 }}>
                <TextField
                  label="Group name" fullWidth size="small" value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  placeholder="e.g. Nyumba ya Musyoka, Burial Committee"
                />
                <Typography variant="caption" color="text.secondary">
                  Choose the members — only they will see this room.
                </Typography>
              </Box>
              <List dense sx={{ maxHeight: 300, overflowY: 'auto' }}>
                {members.map((m) => (
                  <ListItem key={m.id} disablePadding
                    secondaryAction={<Checkbox edge="end" checked={pickedIds.includes(m.id)} onChange={() => togglePick(m.id)} />}>
                    <ListItemButton onClick={() => togglePick(m.id)}>
                      <ListItemAvatar>
                        <Avatar src={m.photo ? photoThumb(m.photo) : undefined}>{m.name.charAt(0)}</Avatar>
                      </ListItemAvatar>
                      <ListItemText primary={m.name} secondary={m.role !== 'MEMBER' ? m.role : undefined} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Box>
          ) : (
            <List dense sx={{ maxHeight: 360, overflowY: 'auto' }}>
              {members.map((m) => (
                <ListItemButton key={m.id} onClick={() => startDm(m)}>
                  <ListItemAvatar>
                    <Avatar src={m.photo ? photoThumb(m.photo) : undefined}>{m.name.charAt(0)}</Avatar>
                  </ListItemAvatar>
                  <ListItemText primary={m.name} secondary={m.role !== 'MEMBER' ? m.role : undefined} />
                </ListItemButton>
              ))}
            </List>
          )}
        </DialogContent>
        {newTab === 0 && (
          <DialogActions>
            <Button onClick={() => setNewOpen(false)}>Cancel</Button>
            <Button variant="contained" disabled={creating || !groupName.trim() || pickedIds.length === 0}
              onClick={handleCreateGroup}
              sx={{ background: 'linear-gradient(135deg, #059669 0%, #065F46 100%)' }}>
              {creating ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : `Create (${pickedIds.length + 1})`}
            </Button>
          </DialogActions>
        )}
      </Dialog>

      {/* Group info / management */}
      {active?.kind === 'group' && (
        <GroupInfoDialog
          open={infoOpen}
          onClose={() => setInfoOpen(false)}
          groupId={active.groupId || active.room.slice('group:'.length)}
          allMembers={members}
          loadDirectory={async () => setMembers(await getChatMembers().catch(() => []))}
          onLeftOrDeleted={async () => {
            setInfoOpen(false);
            setActive(null);
            await loadConversations();
          }}
          onChanged={loadConversations}
          currentUserRole={currentUser?.role}
          onlineUserIds={onlineUserIds}
        />
      )}

      {/* Room members — who belongs here and who's online right now.
          Everyone listed will see messages whenever they're next online,
          even if they show offline now. */}
      <Dialog open={roomMembersOpen} onClose={() => setRoomMembersOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{active?.title || 'Members'}</DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ px: 2, pt: 1, display: 'block' }}>
            {liveOnlineCount} online now • {roomMembers.length} member{roomMembers.length === 1 ? '' : 's'} total.
            Everyone here will see new messages next time they open the app.
          </Typography>
          <List dense>
            {liveRoomMembers.map((m) => (
              <ListItem key={m.id}>
                <ListItemAvatar>
                  <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                    <Avatar src={m.photo ? photoThumb(m.photo) : undefined}>{m.name.charAt(0)}</Avatar>
                    <Box sx={{
                      position: 'absolute', bottom: 0, right: 0, width: 12, height: 12,
                      borderRadius: '50%', border: '2px solid white',
                      bgcolor: m.online ? '#22C55E' : '#B0B0B0',
                    }} />
                  </Box>
                </ListItemAvatar>
                <ListItemText
                  primary={<>{m.name}{m.isMe && ' (You)'}</>}
                  secondary={m.online ? 'Online now' : 'Offline — will see it when back'}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoomMembersOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Per-message actions: Reply / Edit / Delete */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMessageMenu}>
        <MenuItem onClick={() => { setReplyTarget(menuMessage); closeMessageMenu(); }}>
          <MenuItemIcon><ReplyIcon fontSize="small" /></MenuItemIcon>
          <MenuItemText>Reply</MenuItemText>
        </MenuItem>
        {menuMessage && String(menuMessage.sender?.user) === myId && (
          <MenuItem onClick={startEdit}>
            <MenuItemIcon><EditIcon fontSize="small" /></MenuItemIcon>
            <MenuItemText>Edit</MenuItemText>
          </MenuItem>
        )}
        <MenuItem onClick={askDelete} sx={{ color: '#c62828' }}>
          <MenuItemIcon><DeleteIcon fontSize="small" sx={{ color: '#c62828' }} /></MenuItemIcon>
          <MenuItemText>Delete</MenuItemText>
        </MenuItem>
      </Menu>

      {/* Delete confirmation — WhatsApp's two choices, with the same
          1-hour window WhatsApp uses for "delete for everyone" */}
      <Dialog open={Boolean(deleteConfirm)} onClose={() => setDeleteConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete message?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {(() => {
              if (!deleteConfirm) return '';
              const isMine = String(deleteConfirm.sender?.user) === myId;
              if (!isMine) return 'This removes the message from your own view only.';
              const withinWindow = Date.now() - new Date(deleteConfirm.createdAt).getTime() <= 60 * 60 * 1000;
              return withinWindow
                ? 'You can remove it just from your own view, or delete it for everyone in this chat.'
                : 'It has been over an hour, so this can only be removed from your own view now.';
            })()}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 0.5 }}>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button onClick={() => doDelete(false)}>Delete for me</Button>
          {deleteConfirm
            && String(deleteConfirm.sender?.user) === myId
            && Date.now() - new Date(deleteConfirm.createdAt).getTime() <= 60 * 60 * 1000 && (
            <Button color="error" onClick={() => doDelete(true)}>Delete for everyone</Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

/* ==================== Swipeable message bubble ====================
 * Swipe right (touch on phone, or click-and-drag with a mouse) to reply —
 * the same gesture WhatsApp trained everyone on. touch-action: 'pan-y'
 * keeps normal vertical scrolling native and undisturbed; our code only
 * engages once a horizontal drag is detected.
 */
const SWIPE_MAX = 64;
const SWIPE_THRESHOLD = 44;

const SwipeableMessage = ({ message: m, mine, showName, tickState, highlighted, onReply, onJumpToQuote, onOpenMenu, registerRef }) => {
  const [dx, setDx] = useState(0);
  const dragRef = useRef({ dragging: false, startX: 0, startY: 0, locked: null });
  const deleted = Boolean(m.deletedForEveryone);

  const onPointerDown = (e) => {
    if (deleted) return;
    dragRef.current = { dragging: true, startX: e.clientX, startY: e.clientY, locked: null };
  };

  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d.dragging) return;
    const deltaX = e.clientX - d.startX;
    const deltaY = e.clientY - d.startY;

    // Decide once, early, whether this gesture is a horizontal swipe or a
    // vertical scroll — so we never fight the page's natural scrolling.
    if (d.locked === null && (Math.abs(deltaX) > 6 || Math.abs(deltaY) > 6)) {
      d.locked = Math.abs(deltaX) > Math.abs(deltaY) ? 'horizontal' : 'vertical';
    }
    if (d.locked !== 'horizontal') return;

    setDx(Math.max(0, Math.min(deltaX, SWIPE_MAX)));
  };

  const endDrag = () => {
    const d = dragRef.current;
    if (d.dragging && dx >= SWIPE_THRESHOLD) {
      onReply();
      if (navigator.vibrate) navigator.vibrate(8); // tiny haptic tick, Android
    }
    dragRef.current = { dragging: false, startX: 0, startY: 0, locked: null };
    setDx(0);
  };

  return (
    <Box
      ref={registerRef}
      sx={{
        display: 'flex', alignItems: 'center', gap: 0.5, mb: 1, touchAction: 'pan-y',
        justifyContent: mine ? 'flex-end' : 'flex-start',
        }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onPointerLeave={() => dragRef.current.dragging && endDrag()}
    >
      {/* Reply icon that reveals itself as you swipe, filling in toward full
          strength right at the trigger threshold */}
      {!deleted && (
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: 32, height: 32, flexShrink: 0, ml: mine ? 0 : -1, mr: mine ? -1 : 0,
          opacity: Math.min(dx / SWIPE_THRESHOLD, 1),
          transform: `scale(${0.6 + 0.4 * Math.min(dx / SWIPE_THRESHOLD, 1)})`,
          color: '#059669',
        }}>
          <ReplyIcon fontSize="small" />
        </Box>
      )}

      <Paper elevation={0} sx={{
        px: 1.5, py: 1, maxWidth: '78%', borderRadius: 3,
        bgcolor: highlighted ? '#FEF3C7' : (mine ? '#059669' : '#fff'),
        color: mine && !highlighted ? '#fff' : 'inherit',
        border: mine ? 'none' : '1px solid #eee',
        transform: `translateX(${dx}px)`,
        transition: dragRef.current.dragging ? 'none' : 'transform 0.2s ease, background-color 0.4s ease',
        cursor: deleted ? 'default' : 'grab',
      }}>
        {showName && !deleted && (
          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', color: '#065F46' }}>
            {m.sender?.name}
          </Typography>
        )}

        {deleted ? (
          <Typography variant="body2" sx={{ fontStyle: 'italic', opacity: 0.7 }}>
            This message was deleted
          </Typography>
        ) : (
          <>
            {m.replyTo && m.replyTo.text && (
              <Box
                onClick={onJumpToQuote}
                sx={{
                  borderLeft: '3px solid',
                  borderColor: mine ? 'rgba(255,255,255,0.6)' : '#059669',
                  pl: 1, mb: 0.5, opacity: 0.85, cursor: 'pointer',
                  bgcolor: mine ? 'rgba(255,255,255,0.12)' : '#f1f5f4',
                  borderRadius: 1, py: 0.3,
                  '&:hover': { opacity: 1 },
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
                  {m.replyTo.senderName}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 220 }}>
                  {m.replyTo.text}
                </Typography>
              </Box>
            )}

            {m.attachment && m.attachment.type === 'image' && (
              <Box
                component="a" href={m.attachment.url} target="_blank" rel="noopener noreferrer"
                sx={{ display: 'block', mb: m.text ? 0.75 : 0 }}
              >
                <Box
                  component="img" src={m.attachment.url} alt={m.attachment.name || 'shared photo'}
                  sx={{ maxWidth: 240, maxHeight: 240, borderRadius: 2, display: 'block' }}
                />
              </Box>
            )}
            {m.attachment && m.attachment.type === 'pdf' && (
              <Box
                component="a" href={m.attachment.url} target="_blank" rel="noopener noreferrer"
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1, p: 1, mb: m.text ? 0.75 : 0,
                  borderRadius: 2, textDecoration: 'none', color: 'inherit',
                  bgcolor: mine ? 'rgba(255,255,255,0.15)' : '#f1f5f4',
                  border: mine ? 'none' : '1px solid #e0e0e0',
                }}
              >
                <PictureAsPdfIcon sx={{ color: mine ? '#fff' : '#c62828' }} />
                <Typography variant="caption" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
                  {m.attachment.name || 'Document.pdf'}
                </Typography>
              </Box>
            )}

            {m.text && (
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.text}</Typography>
            )}
          </>
        )}

        <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.3, opacity: 0.7 }}>
          {m.editedAt && !deleted ? 'edited · ' : ''}{timeLabel(m.createdAt)}
          {mine && !deleted && tickState && (
            tickState === 'sent'
              ? <DoneIcon sx={{ fontSize: 15 }} />
              : <DoneAllIcon sx={{ fontSize: 15, color: tickState === 'read' ? '#EAB308' : 'inherit' }} />
          )}
        </Typography>
      </Paper>

      {/* Message actions menu — reply, and (for your own messages) edit or
          delete. Always visible on mobile (no hover there); hover-reveal
          on desktop. */}
      {!deleted && (
        <IconButton size="small" onClick={onOpenMenu} aria-label="Message options"
          sx={{ width: 28, height: 28, flexShrink: 0, color: 'text.secondary' }}>
          <MoreVertIcon fontSize="inherit" />
        </IconButton>
      )}
    </Box>
  );
};

/* ==================== Group info dialog ==================== */

const GroupInfoDialog = ({ open, onClose, groupId, allMembers, loadDirectory, onLeftOrDeleted, onChanged, currentUserRole, onlineUserIds }) => {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [adding, setAdding] = useState(false);
  const [addIds, setAddIds] = useState([]);

  const load = useCallback(async () => {
    try {
      setInfo(await getGroupInfo(groupId));
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load the group.');
    }
  }, [groupId]);

  useEffect(() => {
    if (open) {
      load();
      setAdding(false);
      setAddIds([]);
      loadDirectory();
    }
  }, [open, load, loadDirectory]);

  if (!open) return null;

  const memberIds = new Set((info?.members || []).map((m) => String(m.id)));
  const addable = (allMembers || []).filter((m) => !memberIds.has(String(m.id)));
  const isAppAdmin = ['ADMIN', 'PRESIDENT'].includes(currentUserRole);

  const act = async (name, fn, after) => {
    setBusy(name);
    setError('');
    try {
      await fn();
      if (after === 'reload') { await load(); await onChanged(); }
      if (after === 'exit') await onLeftOrDeleted();
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong.');
    } finally {
      setBusy('');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{info?.name || 'Group'}</DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        {info && (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ px: 2, pt: 1, display: 'block' }}>
              Created by {info.createdBy?.name} • {info.members.length} member{info.members.length === 1 ? '' : 's'}
            </Typography>
            <List dense>
              {info.members.map((m) => {
                const online = onlineUserIds?.has(String(m.id));
                return (
                  <ListItem key={m.id}
                    secondaryAction={
                      info.isCreator && !m.isCreator ? (
                        <IconButton edge="end" color="error" disabled={busy !== ''}
                          aria-label={`Remove ${m.name}`}
                          onClick={() => act('remove', () => updateGroupMembers(groupId, { remove: [m.id] }), 'reload')}>
                          <PersonRemoveIcon fontSize="small" />
                        </IconButton>
                      ) : null
                    }>
                    <ListItemAvatar>
                      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                        <Avatar src={m.photo ? photoThumb(m.photo) : undefined}>{m.name.charAt(0)}</Avatar>
                        <Box sx={{
                          position: 'absolute', bottom: 0, right: 0, width: 12, height: 12,
                          borderRadius: '50%', border: '2px solid white',
                          bgcolor: online ? '#22C55E' : '#B0B0B0',
                        }} />
                      </Box>
                    </ListItemAvatar>
                    <ListItemText
                      primary={m.name}
                      secondary={m.isCreator ? 'Group creator' : (online ? 'Online now' : 'Offline')}
                    />
                  </ListItem>
                );
              })}
            </List>

            {info.isCreator && (
              adding ? (
                <Box sx={{ borderTop: '1px solid #eee' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ px: 2, pt: 1, display: 'block' }}>
                    Select members to add:
                  </Typography>
                  <List dense sx={{ maxHeight: 200, overflowY: 'auto' }}>
                    {addable.map((m) => (
                      <ListItem key={m.id} disablePadding
                        secondaryAction={<Checkbox edge="end" checked={addIds.includes(m.id)}
                          onChange={() => setAddIds((p) => p.includes(m.id) ? p.filter((x) => x !== m.id) : [...p, m.id])} />}>
                        <ListItemButton onClick={() => setAddIds((p) => p.includes(m.id) ? p.filter((x) => x !== m.id) : [...p, m.id])}>
                          <ListItemAvatar>
                            <Avatar src={m.photo ? photoThumb(m.photo) : undefined}>{m.name.charAt(0)}</Avatar>
                          </ListItemAvatar>
                          <ListItemText primary={m.name} />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                  <Box sx={{ display: 'flex', gap: 1, p: 1.5 }}>
                    <Button size="small" onClick={() => setAdding(false)}>Cancel</Button>
                    <Button size="small" variant="contained" disabled={addIds.length === 0 || busy !== ''}
                      onClick={() => act('add', () => updateGroupMembers(groupId, { add: addIds }), 'reload').then(() => { setAdding(false); setAddIds([]); })}>
                      Add {addIds.length > 0 ? `(${addIds.length})` : ''}
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Button size="small" sx={{ ml: 1.5, mb: 1 }} onClick={() => setAdding(true)}>+ Add members</Button>
              )
            )}
          </>
        )}
        {error && <Alert severity="error" sx={{ m: 1.5 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Box>
          {info && !info.isCreator && info.isMember && (
            <Button color="warning" disabled={busy !== ''}
              onClick={() => act('leave', () => leaveGroup(groupId), 'exit')}>
              Leave group
            </Button>
          )}
          {info && (info.isCreator || isAppAdmin) && (
            <Button color="error" disabled={busy !== ''}
              onClick={() => {
                if (window.confirm(`Delete "${info.name}" and all its messages? This cannot be undone.`)) {
                  act('delete', () => deleteGroup(groupId), 'exit');
                }
              }}>
              Delete group
            </Button>
          )}
        </Box>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChatPage;
